import { ApolloLink, createHttpLink } from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';
import { Operation } from '@apollo/client/link/core';
import { ErrorCode } from '@buy-viasat/types/build/bv';
import { GraphQLFormattedError } from 'graphql';
import { Refetch } from '../types';
import { store } from '../store';
import { apolloClient } from '@buy-viasat/utils';
import { requestAccessToken } from '@buy-viasat/redux/src/auth';
import { appActions } from '@buy-viasat/redux/src/app';

const httpLink = createHttpLink({
  uri: process.env['REACT_APP_GRAPHQL_API_URL'] + '/graphql',
  credentials: 'include',
});

const authLink = setContext((_, { headers }) => {
  const token = store.getState().auth.accessToken;

  return {
    headers: {
      ...headers,
      authorization: token ? `Bearer ${token}` : '',
    },
  };
});

const authenticationError = (err: GraphQLFormattedError): boolean =>
  err?.extensions?.code === ErrorCode.UNAUTHENTICATED;

const updateToken = (operation: Operation): void => {
  store.dispatch(
    requestAccessToken({
      env: process.env['REACT_APP_ENV']!,
      authUrl: process.env['REACT_APP_GRAPHQL_API_URL'] + '/auth',
    }),
  );

  const priorHeaders = operation.getContext().headers;
  operation.setContext({
    headers: {
      ...priorHeaders,
      authorization: store.getState().auth.accessToken,
    },
  });
};

const authError = onError(({ networkError, graphQLErrors, operation, forward }): Refetch => {
  // Exclude intentional 400 responses (e.g. CC# detected by creditCardCheck middleware) from triggering
  // maintenance mode — those are expected errors that should show a generic error modal, not a blank page.
  if (networkError && !('statusCode' in networkError && networkError.statusCode === 400)) {
    store.dispatch(appActions.loadFeatureFlags({}));
    store.dispatch(
      appActions.setFeatureFlag({
        maintenanceMode: true,
      }),
    );
  }
  if (graphQLErrors) {
    const err = graphQLErrors.find(authenticationError);
    if (err) {
      updateToken(operation);
      forward(operation);
    }
  }
});

const logTimeLink = new ApolloLink((operation, forward) => {
  operation.setContext({ start: new Date() });

  return forward(operation).map((data) => {
    return data;
  });
});

apolloClient.setLink(ApolloLink.from([logTimeLink, authLink, authError, httpLink]));

export const client = apolloClient;
